import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Avatar,
    Divider,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Mercor-style toggle switch component
const MercorSwitch = ({ checked, onChange, disabled = false }) => (
    <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        sx={{
            width: 44,
            height: 24,
            padding: 0,
            '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: '2px',
                transitionDuration: '200ms',
                '&.Mui-checked': {
                    transform: 'translateX(20px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                        backgroundColor: '#4f46e5',
                        opacity: 1,
                        border: 0,
                    },
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                width: 20,
                height: 20,
            },
            '& .MuiSwitch-track': {
                borderRadius: 12,
                backgroundColor: '#e5e7eb',
                opacity: 1,
            },
        }}
    />
);

// Mercor-style input classes
const inputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& fieldset': {
            borderColor: '#d1d5db',
        },
        '&:hover fieldset': {
            borderColor: '#d1d5db',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#4f46e5',
            borderWidth: '2px',
        },
    },
    '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
    },
};

const FormSection = ({ title, subtitle, children, sidebarWidth = '250px' }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 5 }, mb: 4, alignItems: 'flex-start' }}>
        <Box sx={{ width: { md: sidebarWidth }, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem', mt: 0.5 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
        <Box sx={{ flex: 1 }}>
            {children}
        </Box>
    </Box>
);

const AccountTab = ({ userProfile, userId, authAxios, onUpdate, setError }) => {
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);
    
    // Avatar state
    const [avatar, setAvatar] = useState('');
    const [useGenerativeAvatar, setUseGenerativeAvatar] = useState(false);
    
    // Email change state
    const [newEmail, setNewEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    
    // Delete account dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    
    // Payout preferences state
    const [payoutConfigured, setPayoutConfigured] = useState(false);

    // Initialize form data from userProfile
    useEffect(() => {
        if (userProfile) {
            setAvatar(userProfile.profile_picture || '');
            setUseGenerativeAvatar(userProfile.use_generative_avatar || false);
            setPayoutConfigured(userProfile.payout_configured || false);
        }
    }, [userProfile]);

    // Handle avatar upload
    const handleAvatarUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setAvatar(e.target.result);
            };
            reader.readAsDataURL(file);
            
            // Upload to server
            const formData = new FormData();
            formData.append('profile_picture', file);
            
            try {
                await authAxios.patch('/self/', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (onUpdate) onUpdate();
            } catch (error) {
                console.error('Failed to upload avatar:', error);
                if (setError) setError('Failed to upload avatar');
            }
        }
    };

    // Handle generative avatar toggle
    const handleGenerativeToggle = async (value) => {
        setUseGenerativeAvatar(value);
        try {
            await authAxios.patch('/self/', {
                use_generative_avatar: value,
            });
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to update avatar preference:', error);
            if (setError) setError('Failed to update avatar preference');
        }
    };

    // Handle email change
    const handleEmailChange = async () => {
        if (!newEmail || !currentPassword) {
            setError('Please fill in all fields');
            return;
        }
        
        setSaving(true);
        try {
            await authAxios.post('/change-email/', {
                new_email: newEmail,
                current_password: currentPassword,
            });
            setNewEmail('');
            setCurrentPassword('');
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to change email:', error);
            if (setError) setError('Failed to change email');
        } finally {
            setSaving(false);
        }
    };

    // Handle account deletion
    const handleDeleteAccount = async () => {
        if (deleteConfirmation !== 'DELETE') {
            setError('Please type DELETE to confirm');
            return;
        }
        
        try {
            await authAxios.delete('/self/');
            // Redirect to login or home page
            window.location.href = '/';
        } catch (error) {
            console.error('Failed to delete account:', error);
            if (setError) setError('Failed to delete account');
        }
    };

    // Configure payout handler
    const handleConfigurePayout = () => {
        // Redirect to payout configuration
        window.open('https://connect.stripe.com/setup', '_blank');
    };

    return (
        <Box sx={{ maxWidth: '900px', mx: 'auto', py: 4 }}>
            {/* Profile Picture Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Profile Picture
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection title="Avatar">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar
                                src={avatar}
                                sx={{ 
                                    width: 56, 
                                    height: 56,
                                    bgcolor: '#4f46e5',
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                }}
                            >
                                {userProfile?.first_name?.[0] || 'U'}
                            </Avatar>
                            <Box
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    bgcolor: '#4f46e5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    border: '2px solid white',
                                }}
                            >
                                <CameraAltIcon sx={{ color: 'white', fontSize: 12 }} />
                            </Box>
                        </Box>
                        <Box>
                            <Button
                                variant="outlined"
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: '#d1d5db',
                                    color: '#374151',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    '&:hover': { borderColor: '#4f46e5', bgcolor: 'transparent' },
                                }}
                            >
                                Upload new photo
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleAvatarUpload}
                            />
                        </Box>
                    </Box>
                </FormSection>

                <FormSection 
                    title="Use generative profile pictures" 
                    subtitle="Generate unique AI avatars"
                >
                    <MercorSwitch
                        checked={useGenerativeAvatar}
                        onChange={handleGenerativeToggle}
                    />
                </FormSection>
            </Box>

            {/* Payout Preferences Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Payout Preferences
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                {!payoutConfigured && (
                    <Box sx={{ 
                        bgcolor: '#fef3c7', 
                        border: '1px solid #fcd34d',
                        borderRadius: '8px',
                        p: 3,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                    }}>
                        <WarningAmberIcon sx={{ color: '#d97706', mt: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600, color: '#92400e', mb: 0.5 }}>
                                Payout setup required
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#92400e', mb: 2 }}>
                                Set up your payout preferences to receive payments for completed work.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleConfigurePayout}
                                sx={{
                                    bgcolor: '#d97706',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    '&:hover': { bgcolor: '#b45309' },
                                }}
                            >
                                Configure Payout
                            </Button>
                        </Box>
                    </Box>
                )}
                
                {payoutConfigured && (
                    <FormSection title="Payment method">
                        <Typography variant="body2" sx={{ color: '#059669' }}>
                            ✓ Payout configured via Stripe
                        </Typography>
                    </FormSection>
                )}
            </Box>

            {/* Change Email Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Change Email
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection title="Current email">
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {userProfile?.email || 'Loading...'}
                    </Typography>
                </FormSection>

                <FormSection title="New email">
                    <TextField
                        fullWidth
                        size="small"
                        type="email"
                        placeholder="Enter new email address"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        sx={inputStyles}
                    />
                </FormSection>

                <FormSection title="Current password">
                    <TextField
                        fullWidth
                        size="small"
                        type="password"
                        placeholder="Enter your current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        sx={inputStyles}
                    />
                </FormSection>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleEmailChange}
                        disabled={saving || !newEmail || !currentPassword}
                        sx={{
                            bgcolor: '#4f46e5',
                            textTransform: 'none',
                            px: 3,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            '&:hover': { bgcolor: '#4338ca' },
                            '&.Mui-disabled': { bgcolor: '#c7d2fe' },
                        }}
                    >
                        {saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Update Email'}
                    </Button>
                </Box>
            </Box>

            {/* Delete Account Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Danger Zone
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <Box sx={{ 
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    p: 3,
                }}>
                    <Typography sx={{ fontWeight: 600, color: '#991b1b', mb: 1 }}>
                        Delete Account
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                        Once you delete your account, there is no going back. Please be certain.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={{
                            color: '#dc2626',
                            borderColor: '#dc2626',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            '&:hover': { 
                                borderColor: '#dc2626',
                                bgcolor: 'rgba(220, 38, 38, 0.04)',
                            },
                        }}
                    >
                        Delete Account
                    </Button>
                </Box>
            </Box>

            {/* Delete Account Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ fontWeight: 600, color: '#dc2626' }}>
                    Delete Account
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        This action cannot be undone. All your data will be permanently deleted.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Type <strong>DELETE</strong> to confirm:
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        sx={inputStyles}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ textTransform: 'none', color: '#6b7280' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmation !== 'DELETE'}
                        sx={{
                            bgcolor: '#dc2626',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#b91c1c' },
                            '&.Mui-disabled': { bgcolor: '#fecaca' },
                        }}
                    >
                        Delete My Account
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AccountTab;
